<script lang="ts">
  /**
   * L'onglet CRM : une file à traiter, une recherche, une fiche.
   *
   * Les files sont les questions ouvertes du miroir (« sans groupe »,
   * « sans rôle »…) avec leur compte réel. La recherche court-circuite les
   * files et cherche dans TOUT le portefeuille, y compris l'archivé — sinon
   * on ne retrouve jamais la société qu'on a en tête au motif qu'elle est
   * déjà classée.
   *
   * Le chaînage « Suivant » travaille sur une photo de la file prise à
   * l'ouverture de la première fiche : les décisions qu'on vient de prendre
   * retirent des sociétés de la file, et sans cette photo l'ordre se
   * déroberait sous le pouce à chaque tap.
   */

  import { onMount } from "svelte";
  import {
    crmState,
    loadCrm,
    hydrateFromCache,
    visibleCompanies,
    queueCount,
    queueTotal,
    countryTallies,
    companyByUid,
    completenessOf,
    tagUidsOf,
    tierOf,
    QUEUES,
    type QueueId,
  } from "../crm/store.svelte";
  import { classificationOf, type Slot } from "../crm/completeness";
  import { CLASSIFICATION_LABELS, label } from "../crm/types";
  import { country as countryOf } from "../crm/countries";
  import Icon from "./shared/Icon.svelte";
  import { crmSession } from "../crm/session.svelte";
  import { writeState, flushWrites } from "../crm/write.svelte";
  import CrmSignIn from "./CrmSignIn.svelte";
  import CompanySheet from "./CompanySheet.svelte";
  import { haptic } from "../gestures/haptic";

  interface Props {
    /** Piloté par App.svelte pour rester synchrone avec la pile history. */
    selectedUid: string | null;
    onOpen: (uid: string) => void;
    onClose: () => void;
  }

  let { selectedUid, onOpen, onClose }: Props = $props();

  /** Le rendu est plafonné : 1 800 lignes d'un coup asphyxient un iPhone. */
  const PAGE = 60;
  let shown = $state(PAGE);
  let chain = $state<string[]>([]);

  const rows = $derived(visibleCompanies());
  /** Les six créneaux, toujours dans le même ordre : la grille 3×2 de la
   *  ligne doit rester stable d'une société à l'autre, sinon l'œil ne peut
   *  plus comparer deux lignes d'un coup. */
  const SLOTS: Slot[] = ["role", "profile", "group", "country", "tags", "org"];
  const totalCount = $derived(crmState.companies.filter((c) => !c.archived).length);
  const todoCount = $derived(queueCount("incomplete"));
  const company = $derived(companyByUid(selectedUid));
  const searching = $derived(crmState.query.trim().length > 0);

  const nextUid = $derived.by(() => {
    if (!selectedUid) return null;
    const i = chain.indexOf(selectedUid);
    return i >= 0 && i + 1 < chain.length ? chain[i + 1] : null;
  });

  /**
   * Un seul chargement automatique. Sans ce verrou, un échec durable (RLS,
   * réseau) relancerait l'effet à chaque retour de `loading` à false et
   * bouclerait indéfiniment sur la même requête. Après un échec, c'est le
   * bouton « Réessayer » qui redemande.
   */
  let autoLoaded = $state(false);

  function reload() {
    autoLoaded = true;
    void loadCrm();
  }

  onMount(() => {
    void hydrateFromCache();
  });

  // La session peut arriver après le montage (résolution du token persisté).
  $effect(() => {
    if (crmSession.status === "in" && !autoLoaded) reload();
  });

  function selectQueue(id: QueueId) {
    haptic("light");
    crmState.queue = id;
    crmState.query = "";
    shown = PAGE;
    // Le pays reste sélectionné : enchaîner « sans groupe » puis « sans
    // rôle » sur la France est l'usage, pas l'exception. Sauf pour la file
    // « Sans pays », où croiser les deux ne peut donner que du vide.
    if (id === "country") crmState.country = null;
  }

  function selectCountry(code: string | null) {
    haptic("light");
    crmState.country = code;
    shown = PAGE;
  }

  /**
   * Les pays présents dans la file, plus le pays actif même s'il n'y reste
   * rien : sinon changer de file ferait disparaître le chip sélectionné, et
   * la liste vide n'aurait plus d'explication à l'écran.
   */
  const countries = $derived.by(() => {
    const tallies = countryTallies();
    const active = crmState.country;
    if (active && !tallies.some((t) => t.code === active)) {
      return [...tallies, { code: active, count: 0 }];
    }
    return tallies;
  });

  function open(uid: string) {
    haptic("light");
    // Photo de la file au moment où on entre — c'est elle que « Suivant »
    // parcourt, pas la liste qui se vide au fur et à mesure.
    chain = rows.map((c) => c.uid);
    onOpen(uid);
  }

  function goNext() {
    if (!nextUid) return;
    haptic("medium");
    onOpen(nextUid);
    window.scrollTo({ top: 0 });
  }
</script>

{#if crmSession.status === "unknown"}
  <p class="center muted">Connexion au miroir…</p>
{:else if crmSession.status === "out"}
  <CrmSignIn />
{:else if company}
  <CompanySheet {company} onNext={nextUid ? goNext : null} {onClose} />
{:else}
  <div class="list-view">
    <header class="head">
      <h1>Sociétés</h1>
      <div class="meta">
        {totalCount} fiche{totalCount > 1 ? "s" : ""} · {todoCount} à compléter
      </div>
    </header>

    <div class="search">
      <input
        type="search"
        bind:value={crmState.query}
        placeholder="Rechercher une société…"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
      />
    </div>

    {#if !searching}
      <nav class="pills no-scrollbar" aria-label="Files">
        {#each QUEUES as q (q.id)}
          <button
            class="pill"
            class:active={crmState.queue === q.id}
            onclick={() => selectQueue(q.id)}
          >
            {q.label}
            <span class="count">{queueCount(q.id)}</span>
          </button>
        {/each}
      </nav>

      {#if countries.length > 1 && crmState.queue !== "country"}
        <nav class="pills countries no-scrollbar" aria-label="Pays">
          <button
            class="pill"
            class:active={crmState.country === null}
            onclick={() => selectCountry(null)}
          >
            Tous
            <span class="count">{queueTotal()}</span>
          </button>
          {#each countries as t (t.code)}
            {@const info = countryOf(t.code)}
            <button
              class="pill"
              class:active={crmState.country === t.code}
              onclick={() => selectCountry(t.code)}
              aria-label={info?.name ?? t.code}
            >
              <span class="flag">{info?.flag ?? "🏳"}</span>
              {#if crmState.country === t.code}{info?.name ?? t.code}{/if}
              <span class="count">{t.count}</span>
            </button>
          {/each}
        </nav>
      {/if}
    {/if}

    {#if crmState.error}
      <p class="err">{crmState.error}</p>
      <button class="more" onclick={reload} disabled={crmState.loading}>
        {crmState.loading ? "Chargement…" : "Réessayer"}
      </button>
    {/if}
    {#if writeState.error}
      <p class="err">{writeState.error}</p>
    {/if}
    {#if writeState.pending > 0}
      <button class="pending" onclick={() => void flushWrites()}>
        {writeState.pending} modification{writeState.pending > 1 ? "s" : ""} en attente
        d'envoi — toucher pour réessayer
      </button>
    {/if}

    {#if rows.length === 0}
      <p class="center muted">
        {#if crmState.loading}
          Chargement du miroir…
        {:else if searching}
          Aucune société ne correspond.
        {:else}
          File vide — tout est classé ici.
        {/if}
      </p>
    {:else}
      <ul class="rows">
        {#each rows.slice(0, shown) as c (c.uid)}
          {@const comp = completenessOf(c)}
          {@const tier = tierOf(c.tier)}
          {@const cls = classificationOf(c)}
          <li>
            <button class="row" onclick={() => open(c.uid)}>
              <span class="main">
                <span class="name">{c.name}</span>
                <span class="sub">
                  {#if c.country_code}{countryOf(c.country_code)?.flag ?? ""}{/if}
                  {[c.domain, c.city].filter(Boolean).join(" · ") || "—"}
                </span>
                <span class="badges">
                  {#each cls as v, vi (v)}
                    <span class="badge" class:lead={vi === 0}>
                      {label(CLASSIFICATION_LABELS, v)}
                    </span>
                  {/each}
                  {#if tier}<span class="badge tier">{tier.label}</span>{/if}
                  {#if tagUidsOf(c.uid).length > 0}
                    <span class="badge">{tagUidsOf(c.uid).length} ét.</span>
                  {/if}
                </span>
              </span>
              {#if c.reviewed_at}
                <span class="checked" aria-label="Classée"><Icon name="check" size={12} /></span>
              {/if}
              <span class="dots" aria-label={comp.done ? "Complète" : "Incomplète"}>
                {#each SLOTS as slot (slot)}
                  <span
                    class="dot"
                    class:on={comp.filled[slot]}
                    class:req={comp.required.includes(slot) && !comp.filled[slot]}
                    class:off={!comp.relevant.includes(slot)}
                  ></span>
                {/each}
              </span>
            </button>
          </li>
        {/each}
      </ul>
      {#if rows.length > shown}
        <button class="more" onclick={() => (shown += PAGE)}>
          Afficher plus ({rows.length - shown})
        </button>
      {/if}
    {/if}
  </div>
{/if}

<style>
  .list-view { padding: 0 0 32px; }

  .head {
    padding: calc(env(safe-area-inset-top) + 14px) 16px 0;
  }
  h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
  }
  .meta {
    margin-top: 7px;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }

  .search {
    padding: 12px 16px 10px;
  }
  .search input {
    width: 100%;
    height: 34px;
    padding: 0 14px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    color: var(--fg);
    font-family: var(--font-sans);
    font-size: 16px; /* < 16px déclenche le zoom automatique iOS */
  }
  .search input:focus {
    outline: none;
    border-color: var(--accent);
  }

  .pills {
    display: flex;
    gap: 6px;
    padding: 0 16px 12px;
    border-bottom: 1px solid var(--border-subtle);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  /* Second rail : même grammaire que les files, sans le filet — les deux
     barres forment un seul bloc d'en-tête, pas deux sections. */
  .pills.countries {
    padding-top: 0;
    padding-bottom: 12px;
  }
  .pills:not(.countries):has(+ .countries) {
    border-bottom: 0;
    padding-bottom: 8px;
  }
  .flag {
    font-size: 13px;
    line-height: 1;
  }
  .pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 7px 12px;
    border-radius: 999px;
    font-family: var(--font-sans);
    font-size: 12.5px;
    font-weight: 500;
    background: transparent;
    color: var(--fg-muted);
    border: 0.5px solid var(--border);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .pill.active {
    background: var(--fg);
    color: var(--bg);
    border-color: var(--fg);
  }
  .pill .count {
    font-family: var(--font-mono);
    font-size: 10.5px;
    opacity: 0.7;
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    padding: 11px 16px;
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--border-subtle);
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    font-family: var(--font-sans);
  }
  .row:active {
    background: var(--bg-hover);
  }
  .main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .name {
    font-size: 13.5px;
    font-weight: 550;
    color: var(--fg);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-size: 11.5px;
    color: var(--fg-subtle);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
  }
  /* Un seul jeu de couleurs : sur une ligne de 375px, sept teintes de rôle
     redeviennent du bruit. Le rôle dominant prend l'accent, le groupe prend
     le vert, tout le reste est neutre. */
  .badge {
    font-size: 10px;
    font-weight: 500;
    padding: 1px 6px;
    border-radius: var(--radius-sm);
    background: var(--bg-hover);
    color: var(--fg-muted);
  }
  .badge.lead {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .badge.tier {
    background: var(--good-bg);
    color: var(--good);
  }

  /* Cinq points = les cinq cases de la fiche, dans l'ordre de l'écran.
     Plein = renseigné, cerclé d'accent = exigé et manquant. */
  /* Une fiche déclarée classée ne montre plus de manque : le check remplace
     la lecture des points. */
  .checked {
    flex-shrink: 0;
    color: var(--good);
    line-height: 0;
  }

  /* Six points en 3×2 : un par créneau, dans l'ordre de la fiche. Grille
     fixe, donc deux lignes se comparent d'un coup d'œil. */
  .dots {
    display: grid;
    grid-template-columns: repeat(3, 5px);
    gap: 3px;
    flex-shrink: 0;
  }
  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--bg-hover);
  }
  .dot.on { background: var(--good); }
  .dot.req { background: var(--accent); }
  /* Créneau hors sujet pour cette société : la case reste, vide. */
  .dot.off { background: transparent; }

  .more,
  .pending {
    display: block;
    width: 100%;
    margin: 14px 0;
    padding: 11px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--fg-muted);
    font-family: var(--font-sans);
    font-size: 13px;
    cursor: pointer;
  }
  .pending {
    border-color: color-mix(in srgb, var(--accent) 40%, transparent);
    color: var(--accent);
  }

  .center {
    text-align: center;
    padding: 32px 16px;
  }
  .muted {
    color: var(--fg-subtle);
    font-size: 14px;
  }
  .err {
    color: var(--bad);
    font-size: 13px;
    padding: 4px;
  }
</style>
